import { FC, ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import SelectInput from 'components/inputs/selectInput'
import ToggleInput from 'components/inputs/toggleInput'
import useLayout from 'hooks/useLayout'
import useTheme from 'hooks/useTheme'
import useDateFormat from 'hooks/useDateFormat'
import { ThemePreference } from 'utils/theme'
import { DATE_FORMATS, DATE_FORMAT_LABELS, isDateFormat, isTimeFormat, TIME_FORMATS, TIME_FORMAT_LABELS } from 'utils/dateFormat'
import { useCookieConsent } from '@/providers/CookieConsentProvider'
import { ReactComponent as UpgradeIcon } from 'assets/icons/upgrade.svg'
import { ReactComponent as ListIcon } from 'assets/icons/format_list_bulleted.svg'
import { ReactComponent as BugReportIcon } from 'assets/icons/bug_report.svg'
import { ReactComponent as DescriptionIcon } from 'assets/icons/description.svg'
import { ReactComponent as TutorialsIcon } from 'assets/icons/view_list.svg'
import { ReactComponent as SendIcon } from 'assets/icons/send.svg'
import { ReactComponent as SettingsIcon } from 'assets/icons/settings.svg'
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg'

const FEEDBACK_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSePTShBVxj396H76BoSJ6hS6YHY9_ZEyvPU91qYEH8JtvfJCQ/viewform?usp=dialog'

const linkRowClasses = `
    grid
    grid-cols-[min-content_auto]
    h-10
    items-center
    gap-2
    text-base
    cursor-pointer
    px-4
    bg-white
    rounded-md
    border
    border-gray-400
    [&>svg]:fill-black
    lg:hover:bg-blue-100
`

interface HelpLinkRowProps {
    to: string
    text: string
    icon: ReactNode
    toNewTab?: boolean
}

const HelpLinkRow: FC<HelpLinkRowProps> = ({ to, text, icon, toNewTab }) => {
    const { closeDialog } = useLayout()

    if (toNewTab) {
        return (
            <a
                href={to}
                target="_blank"
                rel="noopener noreferrer"
                className={linkRowClasses}
                onClick={closeDialog}
            >
                {icon}
                <span className="text-black whitespace-nowrap select-none">{text}</span>
            </a>
        )
    }

    return (
        <Link to={to} className={linkRowClasses} onClick={closeDialog}>
            {icon}
            <span className="text-black whitespace-nowrap select-none">{text}</span>
        </Link>
    )
}

type SettingsSection = 'settings' | 'search' | 'help' | 'legal'

interface SettingsTabButtonProps {
    id: SettingsSection
    label: string
    icon: ReactNode
    selected: boolean
    onSelect: (id: SettingsSection) => void
}

const SettingsTabButton: FC<SettingsTabButtonProps> = ({ id, label, icon, selected, onSelect }) => (
    <button
        type="button"
        role="tab"
        id={`help-settings-tab-${id}`}
        aria-selected={selected}
        aria-controls={`help-settings-panel-${id}`}
        onClick={() => onSelect(id)}
        className={`
            grid
            grid-cols-[min-content_auto]
            h-8
            w-full
            items-center
            gap-1.5
            text-sm
            text-left
            cursor-pointer
            px-2.5
            rounded-md
            hover:bg-blue-50
            [&>svg]:h-5
            [&>svg]:w-5
            [&>svg]:fill-black
            ${selected ? '!bg-blue-100 hover:!bg-blue-100' : ''}
        `}
    >
        {icon}
        <span className="text-black whitespace-nowrap select-none">{label}</span>
    </button>
)

const HelpAndSettings = () => {
    const { t, i18n } = useTranslation()
    const { searchDisplayDeleted, setSearchDisplayDeleted } = useLayout()
    const { preference: themePreference, setPreference: setThemePreference } = useTheme()
    const { dateFormat, setDateFormat, timeFormat, setTimeFormat } = useDateFormat()
    const { consent, setConsent } = useCookieConsent()
    const currentLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]
    const [section, setSection] = useState<SettingsSection>('settings')

    const setLanguage = (langCode: string) => {
        i18n.changeLanguage(langCode)
        localStorage.setItem('lang', langCode)
    }

    const tabs: { id: SettingsSection, label: string, icon: ReactNode }[] = [
        { id: 'settings', label: t('buttonSettings'), icon: <SettingsIcon /> },
        { id: 'search', label: t('titleSearchOptions'), icon: <SearchIcon /> },
        { id: 'help', label: t('titleHelp'), icon: <ListIcon /> },
        { id: 'legal', label: t('titleLegal'), icon: <DescriptionIcon /> },
    ]

    return (
        <div className="flex items-stretch gap-4 min-h-[16rem] pb-4">
            <div role="tablist" aria-orientation="vertical" className="flex w-max shrink-0 flex-col gap-1 border-r border-gray-200 pr-3">
                {tabs.map((tab) => (
                    <SettingsTabButton
                        key={tab.id}
                        id={tab.id}
                        label={tab.label}
                        icon={tab.icon}
                        selected={section === tab.id}
                        onSelect={setSection}
                    />
                ))}
            </div>
            <div
                role="tabpanel"
                id={`help-settings-panel-${section}`}
                aria-labelledby={`help-settings-tab-${section}`}
                className="flex min-w-0 flex-1 flex-col gap-y-2"
            >
                {section === 'settings' && (
                    <>
                        <h3>{t('buttonSettings')}</h3>
                        <SelectInput
                            ignore={true}
                            label={t('language')}
                            value={currentLang === 'sv' ? 'sv' : 'en'}
                            onChange={(e) => setLanguage(e.target.value)}
                        >
                            <option value="en">English</option>
                            <option value="sv">Svenska</option>
                        </SelectInput>
                        <SelectInput
                            ignore={true}
                            label={t('labelTheme')}
                            value={themePreference}
                            onChange={(e) => setThemePreference(e.target.value as ThemePreference)}
                        >
                            <option value="bright">{t('themeBright')}</option>
                            <option value="dark">{t('themeDark')}</option>
                            <option value="automatic">{t('themeAutomatic')}</option>
                        </SelectInput>
                        <SelectInput
                            ignore={true}
                            label={t('labelDateFormat')}
                            value={dateFormat}
                            onChange={(e) => {
                                if (isDateFormat(e.target.value)) setDateFormat(e.target.value)
                            }}
                        >
                            {DATE_FORMATS.map((format) => (
                                <option key={format} value={format}>{DATE_FORMAT_LABELS[format]}</option>
                            ))}
                        </SelectInput>
                        <SelectInput
                            ignore={true}
                            label={t('labelTimeFormat')}
                            value={timeFormat}
                            onChange={(e) => {
                                if (isTimeFormat(e.target.value)) setTimeFormat(e.target.value)
                            }}
                        >
                            {TIME_FORMATS.map((format) => (
                                <option key={format} value={format}>
                                    {format === 'automatic' ? t('themeAutomatic') : TIME_FORMAT_LABELS[format]}
                                </option>
                            ))}
                        </SelectInput>
                        <ToggleInput
                            label={t('labelAllowAnalyticsCookies')}
                            checked={consent === 'granted'}
                            onChange={(e) => setConsent(e.target.checked ? 'granted' : 'denied')}
                            helperText={t('cookieSettingsHelper')}
                        />
                    </>
                )}
                {section === 'search' && (
                    <>
                        <h3>{t('titleSearchOptions')}</h3>
                        <ToggleInput
                            label={t('labelDisplayDeleted')}
                            checked={searchDisplayDeleted}
                            onChange={(e) => setSearchDisplayDeleted(e.target.checked)}
                        />
                    </>
                )}
                {section === 'help' && (
                    <>
                        <h3>{t('titleHelp')}</h3>
                        <HelpLinkRow to="/faq" icon={<ListIcon />} text={t('navFaq')} />
                        <HelpLinkRow to="/support" icon={<SendIcon />} text={t('titleSupport')} />
                        <HelpLinkRow to="/tutorials" icon={<TutorialsIcon />} text={t('navTutorials')} />
                        <HelpLinkRow to={FEEDBACK_URL} toNewTab={true} icon={<BugReportIcon />} text={t('buttonFeedback')} />
                        <HelpLinkRow to="/pricing" icon={<UpgradeIcon />} text={t('navPricing')} />
                    </>
                )}
                {section === 'legal' && (
                    <>
                        <h3>{t('titleLegal')}</h3>
                        <HelpLinkRow to="/privacy" icon={<DescriptionIcon />} text={t('titlePrivacy')} />
                        <HelpLinkRow to="/terms" icon={<DescriptionIcon />} text={t('titleTerms')} />
                    </>
                )}
            </div>
        </div>
    )
}

export default HelpAndSettings
